import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import dashboardService from "src/services/dashboardService";
import whatsappService from "src/services/whatsappService";
import failedTxnService from "src/services/failedTxnService";
import vehicleChargingConfigService from "src/services/vehicleChargingConfigService";
import { alertSlice } from "src/store/alert/alert.slice";
import { Loader } from "../Loader";
import CompanyFleetSettings from "./CompanyFleetSettings";

/**
 * Company → Settings tab. Super-admin-only per-company feature toggles.
 *
 * Currently exposes the Dashboard V2 (InsightHub) flag. The read/write use a
 * dedicated, resilient endpoint that is deliberately decoupled from login, so a
 * missing column or transient DB issue never breaks the Company page — the
 * toggle simply shows "off".
 */
export const CompanyDashboardSettings = ({ companyId }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const [waEnabled, setWaEnabled] = useState(false);
  const [ftSaving, setFtSaving] = useState(false);
  const [ftEnabled, setFtEnabled] = useState(false);
  const [vcSaving, setVcSaving] = useState(false);
  const [vcEnabled, setVcEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    if (!companyId) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      setLoading(true);
      const [config, waConfig, ftConfig, vcConfig] = await Promise.all([
        dashboardService.getCompanyDashboardConfig(companyId),
        whatsappService.getCompanyWhatsappConfig(companyId),
        failedTxnService.getCompanyFailedTxnConfig(companyId),
        vehicleChargingConfigService.getCompanyVehicleChargingConfig(companyId),
      ]);
      if (active) {
        setEnabled(config?.dashboardV2Enabled === true);
        setWaEnabled(waConfig?.whatsappEnabled === true);
        setFtEnabled(ftConfig?.failedTxnEnabled === true);
        setVcEnabled(vcConfig?.vehicleChargingEnabled === true);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId]);

  const handleVehicleChargingToggle = async (event) => {
    const next = event.target.checked;
    const previous = vcEnabled;
    setVcEnabled(next); // optimistic
    setVcSaving(true);
    try {
      const result = await vehicleChargingConfigService.updateCompanyVehicleChargingConfig(
        companyId,
        next
      );
      setVcEnabled(result?.vehicleChargingEnabled === true);
      // The dashboard caches the flag per team; drop it so the change shows at once.
      queryClient.invalidateQueries(["vehicleChargingConfig"]);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `Vehicle Charging on the dashboard ${
            result?.vehicleChargingEnabled ? "enabled" : "disabled"
          } for this company`,
          severity: "success",
        })
      );
    } catch (error) {
      setVcEnabled(previous); // roll back
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            "Failed to update Vehicle Charging setting",
          severity: "error",
        })
      );
    } finally {
      setVcSaving(false);
    }
  };

  const handleFailedTxnToggle = async (event) => {
    const next = event.target.checked;
    const previous = ftEnabled;
    setFtEnabled(next); // optimistic
    setFtSaving(true);
    try {
      const result = await failedTxnService.updateCompanyFailedTxnConfig(
        companyId,
        next
      );
      setFtEnabled(result?.failedTxnEnabled === true);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `Failed Transactions view ${
            result?.failedTxnEnabled ? "enabled" : "disabled"
          } for this company`,
          severity: "success",
        })
      );
    } catch (error) {
      setFtEnabled(previous); // roll back
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            "Failed to update Failed Transactions setting",
          severity: "error",
        })
      );
    } finally {
      setFtSaving(false);
    }
  };

  const handleWhatsappToggle = async (event) => {
    const next = event.target.checked;
    const previous = waEnabled;
    setWaEnabled(next); // optimistic
    setWaSaving(true);
    try {
      const result = await whatsappService.updateCompanyWhatsappConfig(
        companyId,
        next
      );
      setWaEnabled(result?.whatsappEnabled === true);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `WhatsApp notifications ${
            result?.whatsappEnabled ? "enabled" : "disabled"
          } for this company`,
          severity: "success",
        })
      );
    } catch (error) {
      setWaEnabled(previous); // roll back
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            "Failed to update WhatsApp setting",
          severity: "error",
        })
      );
    } finally {
      setWaSaving(false);
    }
  };

  const handleToggle = async (event) => {
    const next = event.target.checked;
    const previous = enabled;
    setEnabled(next); // optimistic
    setSaving(true);
    try {
      const result = await dashboardService.updateCompanyDashboardConfig(
        companyId,
        next
      );
      setEnabled(result?.dashboardV2Enabled === true);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `Dashboard V2 ${
            result?.dashboardV2Enabled ? "enabled" : "disabled"
          } for this company`,
          severity: "success",
        })
      );
    } catch (error) {
      setEnabled(previous); // roll back
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            "Failed to update dashboard setting",
          severity: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Dashboard
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle2">
                New Dashboard (InsightHub V2)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, users of this company see the new analytics
                dashboard instead of the classic one.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={enabled}
                  onChange={handleToggle}
                  disabled={saving}
                />
              }
              label={enabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle2">Vehicle Charging</Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, the new dashboard shows a Vehicle Charging section —
                vehicles charging now and per-vehicle charging history (energy and
                state of charge) — for this company and all of its teams.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={vcEnabled}
                  onChange={handleVehicleChargingToggle}
                  disabled={vcSaving}
                />
              }
              label={vcEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 1, mt: 3 }}>
        Fleet
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <CompanyFleetSettings companyId={companyId} />

      <Typography variant="subtitle1" sx={{ mb: 1, mt: 3 }}>
        WhatsApp
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle2">
                WhatsApp notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, this company&apos;s chargers can set a WhatsApp
                phone number (under Charge Point → Settings → Notifications) and
                receive notifications on WhatsApp in addition to email.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={waEnabled}
                  onChange={handleWhatsappToggle}
                  disabled={waSaving}
                />
              }
              label={waEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 1, mt: 3 }}>
        Transactions
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle2">Failed Transactions</Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, this company&apos;s users see the Failed
                Transactions view — the failure-rate KPI and per-charger
                breakdown (0&nbsp;kWh sessions and payments with no session).
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={ftEnabled}
                  onChange={handleFailedTxnToggle}
                  disabled={ftSaving}
                />
              }
              label={ftEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyDashboardSettings;
